/* eslint-disable max-len */
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import LiteracyScreeningBeginningOfYearPanel from '../../../../components/FormLayouts/LiteracyScreening/LiteracyScreeningBeginningOfYearPanel';
import LiteracyScreeningEndOfYearPanel from '../../../../components/FormLayouts/LiteracyScreening/LiteracyScreeningEndOfYearPanel';
import LiteracyScreeningMiddleOfYearPanel from '../../../../components/FormLayouts/LiteracyScreening/LiteracyScreeningMiddleOfYearPanel';
import LiteracyScreeningParentNotificationDatesPanel, {
  LiteracyScreeningNotificationDates,
  LiteracyScreeningNotificationDatesEditState,
} from '../../../../components/FormLayouts/LiteracyScreening/LiteracyScreeningParentNotificationDatesPanel';
import {
  buildSessionGridRows,
  LiteracyScreeningSessionEditState,
  LiteracyScreeningSessionGridRow,
} from '../../../../components/FormLayouts/LiteracyScreening/LiteracyScreeningSessionPanel';
import Modal from '../../../../components/Modal/Modal';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectSelectedDistrict } from '../../../../redux/slices/appSlice';
import {
  selectLiteracyScreeningLEASchools,
  selectLiteracyScreeningLoadingLEASchools,
  selectLiteracyScreeningLoadingOptionSets,
  selectLiteracyScreeningPendingSchool,
  selectLiteracyScreeningSchoolList,
  selectLiteracyScreeningUpdate,
  setLiteracyScreeningPendingSchool,
} from '../../../../redux/slices/literacyScreeningSlice';
import { createNotification } from '../../../../redux/slices/notificationSlice';
import {
  createLiteracyScreening,
  fetchLiteracyScreeningLEASchools,
  fetchLiteracyScreeningOptionSets,
  fetchLiteracyScreeningUpdate,
  updateLiteracyScreening,
} from '../../../../redux/thunks/literacyScreeningThunks';
import { abortPromiseOnUnmount } from '../../../../services/base.service';
import { appRoutePaths } from '../../../../services/route.service';
import { DialogType } from '../../../../types/DialogTypes';
import { LiteracyScreeningGradesModel } from '../../../../types/LiteracyScreeningGradesModel';
import {
  applyLiteracyScreeningSessionRowsToGrades,
  buildLiteracyScreeningSaveModel,
  ensureLiteracyScreeningSessionsForSave,
  isNewLiteracyScreeningId,
  LITERACY_SCREENING_BEGINNING_OF_YEAR_SESSION,
  LITERACY_SCREENING_END_OF_YEAR_SESSION,
  LITERACY_SCREENING_MIDDLE_OF_YEAR_SESSION,
} from '../../../../types/LiteracyScreeningModel';
import { LiteracyScreeningSchoolModel } from '../../../../types/LiteracyScreeningSchoolModel';
import {
  hasIncompleteEditedLiteracyScreeningSession,
  isLiteracyScreeningEoySessionEnabled,
  isLiteracyScreeningMoySessionEnabled,
  isLiteracyScreeningNotificationDateComplete,
  isLiteracyScreeningSessionGridComplete,
} from '../../../../types/Validation/LiteracyScreeningScreenerValidationSchema';
import { isSPO } from '../../../../utilities/userUtilities';
import DistrictSchoolInfoPage from '../DistrictSchoolInfoPage';
import './LiteracyScreeningInput.css';

const EMPTY_GRADES: LiteracyScreeningGradesModel[] = [];
const EMPTY_NOTIFICATION_DATES: LiteracyScreeningNotificationDates = {
  beginningOfYearNotificationDate: '',
  middleOfYearNotificationDate: '',
  endOfYearNotificationDate: '',
};
const EMPTY_GRID_COMPLETE = {
  boy: false,
  moy: false,
  eoy: false,
};

type LiteracyScreeningSessionKey = 'boy' | 'moy' | 'eoy';

const EMPTY_SESSION_FLAGS = {
  hasCommittedEdits: false,
  pendingEditDirty: false,
  pendingEditHasError: false,
};

const LiteracyScreeningSchoolInput = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { leaIdentifier, schoolIdentifier, schoolYear, id } = useParams<{
    leaIdentifier: string;
    schoolIdentifier: string;
    schoolYear: string;
    id: string;
  }>();
  const selectedDistrict = useAppSelector(selectSelectedDistrict);
  const leaSchools = useAppSelector(selectLiteracyScreeningLEASchools);
  const isLoadingOptionSets = useAppSelector(
    selectLiteracyScreeningLoadingOptionSets
  );
  const isLoadingLEASchools = useAppSelector(
    selectLiteracyScreeningLoadingLEASchools
  );
  const pendingSchool = useAppSelector(selectLiteracyScreeningPendingSchool);
  const schoolList = useAppSelector(selectLiteracyScreeningSchoolList);
  const literacyScreeningUpdate = useAppSelector(selectLiteracyScreeningUpdate);

  const [hasCommittedEdits, setHasCommittedEdits] = useState(false);
  const [pendingEditDirty, setPendingEditDirty] = useState(false);
  const [pendingEditHasError, setPendingEditHasError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [sessionGridComplete, setSessionGridComplete] =
    useState(EMPTY_GRID_COMPLETE);
  const [sessionCommittedEdits, setSessionCommittedEdits] =
    useState(EMPTY_GRID_COMPLETE);
  const sessionRowsRef = useRef<
    Record<LiteracyScreeningSessionKey, LiteracyScreeningSessionGridRow[]>
  >({
    boy: [],
    moy: [],
    eoy: [],
  });
  const sessionFlagsRef = useRef<
    Record<
      LiteracyScreeningSessionKey,
      {
        hasCommittedEdits: boolean;
        pendingEditDirty: boolean;
        pendingEditHasError: boolean;
      }
    >
  >({
    boy: { ...EMPTY_SESSION_FLAGS },
    moy: { ...EMPTY_SESSION_FLAGS },
    eoy: { ...EMPTY_SESSION_FLAGS },
  });
  const notificationDatesRef = useRef<LiteracyScreeningNotificationDates>({
    ...EMPTY_NOTIFICATION_DATES,
  });
  const notificationFlagsRef = useRef({
    hasCommittedEdits: false,
    pendingEditHasError: false,
  });

  const selectedSchool = useMemo(() => {
    if (
      pendingSchool?.schoolIdentifier &&
      pendingSchool.schoolIdentifier === schoolIdentifier
    ) {
      return pendingSchool;
    }

    return (
      schoolList.find(
        (school) => school.schoolIdentifier === schoolIdentifier
      ) || null
    );
  }, [pendingSchool, schoolIdentifier, schoolList]);

  const schoolYearValue =
    schoolYear || String(selectedSchool?.schoolYear || '');

  const savedSessionGridComplete = useMemo(
    () => ({
      boy: isLiteracyScreeningSessionGridComplete(
        buildSessionGridRows(
          selectedSchool?.grades || EMPTY_GRADES,
          LITERACY_SCREENING_BEGINNING_OF_YEAR_SESSION
        ),
        schoolYearValue
      ),
      moy: isLiteracyScreeningSessionGridComplete(
        buildSessionGridRows(
          selectedSchool?.grades || EMPTY_GRADES,
          LITERACY_SCREENING_MIDDLE_OF_YEAR_SESSION
        ),
        schoolYearValue
      ),
      eoy: isLiteracyScreeningSessionGridComplete(
        buildSessionGridRows(
          selectedSchool?.grades || EMPTY_GRADES,
          LITERACY_SCREENING_END_OF_YEAR_SESSION
        ),
        schoolYearValue
      ),
    }),
    [schoolYearValue, selectedSchool?.grades]
  );

  const savedNotificationComplete = useMemo(
    () => ({
      boy: isLiteracyScreeningNotificationDateComplete(
        selectedSchool?.beginningOfYearNotificationDate,
        schoolYearValue
      ),
      moy: isLiteracyScreeningNotificationDateComplete(
        selectedSchool?.middleOfYearNotificationDate,
        schoolYearValue
      ),
      eoy: isLiteracyScreeningNotificationDateComplete(
        selectedSchool?.endOfYearNotificationDate,
        schoolYearValue
      ),
    }),
    [
      schoolYearValue,
      selectedSchool?.beginningOfYearNotificationDate,
      selectedSchool?.endOfYearNotificationDate,
      selectedSchool?.middleOfYearNotificationDate,
    ]
  );

  const notificationDateValues = useMemo(
    (): LiteracyScreeningNotificationDates => ({
      beginningOfYearNotificationDate:
        selectedSchool?.beginningOfYearNotificationDate || '',
      middleOfYearNotificationDate:
        selectedSchool?.middleOfYearNotificationDate || '',
      endOfYearNotificationDate:
        selectedSchool?.endOfYearNotificationDate || '',
    }),
    [selectedSchool]
  );

  const recomputeSaveState = useCallback((): void => {
    const flags = sessionFlagsRef.current;
    const notificationFlags = notificationFlagsRef.current;
    setHasCommittedEdits(
      flags.boy.hasCommittedEdits ||
      flags.moy.hasCommittedEdits ||
      flags.eoy.hasCommittedEdits ||
      notificationFlags.hasCommittedEdits
    );
    setPendingEditDirty(
      flags.boy.pendingEditDirty ||
      flags.moy.pendingEditDirty ||
      flags.eoy.pendingEditDirty
    );
    setPendingEditHasError(
      flags.boy.pendingEditHasError ||
      flags.moy.pendingEditHasError ||
      flags.eoy.pendingEditHasError ||
      notificationFlags.pendingEditHasError
    );
  }, []);

  useEffect(() => {
    const optionSetsPromise: unknown = dispatch(
      fetchLiteracyScreeningOptionSets({
        module: 'LiteracyScreening',
        page: 'InputPage',
      })
    );

    return () => {
      abortPromiseOnUnmount(optionSetsPromise);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!schoolYear) {
      return;
    }

    const literacyScreeningUpdatePromise: unknown = dispatch(
      fetchLiteracyScreeningUpdate(schoolYear)
    );

    return () => {
      abortPromiseOnUnmount(literacyScreeningUpdatePromise);
    };
  }, [dispatch, schoolYear]);

  useEffect(() => {
    if (!schoolYear || selectedSchool) {
      return;
    }

    const leaSchoolsPromise: unknown = dispatch(
      fetchLiteracyScreeningLEASchools(schoolYear)
    );

    return () => {
      abortPromiseOnUnmount(leaSchoolsPromise);
    };
  }, [dispatch, schoolYear, selectedSchool]);

  const applySessionEditState = useCallback(
    (
      key: LiteracyScreeningSessionKey,
      state: LiteracyScreeningSessionEditState
    ): void => {
      sessionRowsRef.current[key] = state.rows;
      sessionFlagsRef.current[key] = {
        hasCommittedEdits: state.hasCommittedEdits,
        pendingEditDirty: state.pendingEditDirty,
        pendingEditHasError: state.pendingEditHasError,
      };

      setSessionGridComplete({
        boy: isLiteracyScreeningSessionGridComplete(
          sessionRowsRef.current.boy,
          schoolYearValue
        ),
        moy: isLiteracyScreeningSessionGridComplete(
          sessionRowsRef.current.moy,
          schoolYearValue
        ),
        eoy: isLiteracyScreeningSessionGridComplete(
          sessionRowsRef.current.eoy,
          schoolYearValue
        ),
      });
      setSessionCommittedEdits({
        boy: sessionFlagsRef.current.boy.hasCommittedEdits,
        moy: sessionFlagsRef.current.moy.hasCommittedEdits,
        eoy: sessionFlagsRef.current.eoy.hasCommittedEdits,
      });

      recomputeSaveState();
    },
    [recomputeSaveState, schoolYearValue]
  );

  const handleBoyEditStateChange = useCallback(
    (state: LiteracyScreeningSessionEditState): void => {
      applySessionEditState('boy', state);
    },
    [applySessionEditState]
  );

  const handleMoyEditStateChange = useCallback(
    (state: LiteracyScreeningSessionEditState): void => {
      applySessionEditState('moy', state);
    },
    [applySessionEditState]
  );

  const handleEoyEditStateChange = useCallback(
    (state: LiteracyScreeningSessionEditState): void => {
      applySessionEditState('eoy', state);
    },
    [applySessionEditState]
  );

  const handleNotificationEditStateChange = useCallback(
    (state: LiteracyScreeningNotificationDatesEditState): void => {
      notificationDatesRef.current = state.values;
      notificationFlagsRef.current = {
        hasCommittedEdits: state.dirty,
        pendingEditHasError: state.hasError,
      };
      recomputeSaveState();
    },
    [recomputeSaveState]
  );

  const isMoyPanelEnabled = isLiteracyScreeningMoySessionEnabled(
    savedSessionGridComplete.boy,
    savedNotificationComplete.boy
  );
  const isEoyPanelEnabled = isLiteracyScreeningEoySessionEnabled(
    savedSessionGridComplete.boy,
    savedSessionGridComplete.moy,
    savedNotificationComplete.boy,
    savedNotificationComplete.moy
  );
  const isSchoolInputReadOnly = isSPO() || !literacyScreeningUpdate;

  const hasIncompleteEditedSession =
    hasIncompleteEditedLiteracyScreeningSession([
      {
        hasCommittedEdits: sessionCommittedEdits.boy,
        isComplete: sessionGridComplete.boy,
      },
      {
        hasCommittedEdits: sessionCommittedEdits.moy,
        isComplete: sessionGridComplete.moy,
      },
      {
        hasCommittedEdits: sessionCommittedEdits.eoy,
        isComplete: sessionGridComplete.eoy,
      },
    ]);
  const hasDataChanges = hasCommittedEdits || pendingEditDirty;
  const isSaveDisabled =
    isSchoolInputReadOnly ||
    !hasCommittedEdits ||
    hasIncompleteEditedSession ||
    pendingEditHasError ||
    isSaving ||
    !selectedSchool;

  const saveTooltip = (): string => {
    if (!hasCommittedEdits) {
      return '**No changes have been made**';
    }
    if (hasIncompleteEditedSession) {
      return 'Complete all grade rows before saving';
    }
    if (pendingEditHasError) {
      return 'Fix validation errors before saving';
    }
    return 'Literacy Screening data saved';
  };

  const buildUpdatedSchool = (
    school: LiteracyScreeningSchoolModel,
    nextId: string
  ): LiteracyScreeningSchoolModel => ({
    ...school,
    id: nextId,
    leaIdentifier:
      school.leaIdentifier ||
      leaIdentifier ||
      leaSchools.leaIdentifier ||
      selectedDistrict.districtId ||
      '',
    schoolIdentifier: school.schoolIdentifier || schoolIdentifier || '',
    schoolYear: Number(schoolYear || school.schoolYear || 0),
    grades: applyLiteracyScreeningSessionRowsToGrades(school.grades, {
      beginningOfYear: sessionRowsRef.current.boy,
      middleOfYear: sessionRowsRef.current.moy,
      endOfYear: sessionRowsRef.current.eoy,
    }),
    beginningOfYearNotificationDate:
      notificationDatesRef.current.beginningOfYearNotificationDate,
    middleOfYearNotificationDate:
      notificationDatesRef.current.middleOfYearNotificationDate,
    endOfYearNotificationDate:
      notificationDatesRef.current.endOfYearNotificationDate,
  });

  const handleSave = async (): Promise<void> => {
    if (isSaveDisabled || !selectedSchool) {
      return;
    }

    const payload = buildLiteracyScreeningSaveModel(
      selectedSchool,
      sessionRowsRef.current.boy,
      {
        id,
        schoolYear,
        leaIdentifier:
          leaIdentifier ||
          selectedSchool.leaIdentifier ||
          leaSchools.leaIdentifier ||
          selectedDistrict.districtId,
        schoolIdentifier,
      },
      {
        middleOfYear: sessionRowsRef.current.moy,
        endOfYear: sessionRowsRef.current.eoy,
      },
      notificationDatesRef.current
    );
    payload.grades = payload.grades.map((grade) => ({
      ...grade,
      screenerSession: ensureLiteracyScreeningSessionsForSave(
        grade.screenerSession
      ),
    }));

    setIsSaving(true);
    try {
      if (isNewLiteracyScreeningId(id)) {
        const createdId = await dispatch(
          createLiteracyScreening(payload)
        ).unwrap();
        if (typeof createdId !== 'string' || createdId.trim() === '') {
          return;
        }
        dispatch(
          setLiteracyScreeningPendingSchool(
            buildUpdatedSchool(selectedSchool, createdId)
          )
        );
        dispatch(
          createNotification({
            severity: 'success',
            children: 'Literacy Screening data saved',
          })
        );
        navigate(
          appRoutePaths.LiteracyScreeningSchoolInput(
            payload.leaIdentifier,
            payload.schoolIdentifier,
            payload.schoolYear,
            createdId
          ),
          { replace: true }
        );
      } else {
        await dispatch(
          updateLiteracyScreening({
            id: id || payload.id,
            model: payload,
          })
        ).unwrap();
        dispatch(
          setLiteracyScreeningPendingSchool(
            buildUpdatedSchool(selectedSchool, id || payload.id)
          )
        );
        dispatch(
          createNotification({
            severity: 'success',
            children: 'Literacy Screening data saved',
          })
        );
      }
    } catch {
      // API errors are surfaced by the screening thunks.
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = (): void => {
    if (!hasDataChanges) {
      navigate(appRoutePaths.LiteracyScreeningInput(schoolYear || ''));
      return;
    }

    setShowCancelModal(true);
  };

  const handleYesInCancel = (): void => {
    setShowCancelModal(false);
    navigate(appRoutePaths.LiteracyScreeningInput(schoolYear || ''));
  };

  const handleNoInCancel = (): void => {
    setShowCancelModal(false);
  };

  return (
    <DistrictSchoolInfoPage
      pageClass="manual-input data-input--literacy-screening--input"
      loadingDataId="literacy-screening-school-input-loader"
      isLoading={isLoadingOptionSets || isLoadingLEASchools}
      backBarLocation={appRoutePaths.LiteracyScreeningInput(schoolYear || '')}
    >
      <div className="heading-button-error-container">
        <div className="manual-button-container">
          <span className="subheading" data-testid="component-heading-text">
            Literacy Screening
          </span>
          <div className="button-group">
            <p className="required-label">* Required Field</p>
            <ActionButton
              classes="button no-wrap-text cancel-button"
              onClick={handleCancel}
              dataTestId="cancel-button"
              tooltipText="Navigate to the last page"
            >
              <>Cancel</>
            </ActionButton>
            <ActionButton
              classes="button--secondary no-wrap-text submit-button"
              dataTestId="submit-button"
              disabled={isSaveDisabled}
              tooltipText={saveTooltip()}
              loading={isSaving}
              onClick={handleSave}
            >
              <>Save</>
            </ActionButton>
          </div>
        </div>
      </div>
      <div
        data-testid="literacy-screening-school-input"
        data-lea-identifier={leaIdentifier}
        data-school-identifier={schoolIdentifier}
        data-school-year={schoolYear}
        data-id={id}
      >
        <LiteracyScreeningBeginningOfYearPanel
          grades={selectedSchool?.grades || EMPTY_GRADES}
          schoolYear={schoolYearValue}
          schoolName={selectedSchool?.schoolName || ''}
          disabled={isSchoolInputReadOnly}
          onEditStateChange={handleBoyEditStateChange}
        />
        <LiteracyScreeningMiddleOfYearPanel
          grades={selectedSchool?.grades || EMPTY_GRADES}
          schoolYear={schoolYearValue}
          disabled={isSchoolInputReadOnly || !isMoyPanelEnabled}
          onEditStateChange={handleMoyEditStateChange}
        />
        <LiteracyScreeningEndOfYearPanel
          grades={selectedSchool?.grades || EMPTY_GRADES}
          schoolYear={schoolYearValue}
          disabled={isSchoolInputReadOnly || !isEoyPanelEnabled}
          onEditStateChange={handleEoyEditStateChange}
        />
        <LiteracyScreeningParentNotificationDatesPanel
          schoolYear={schoolYearValue}
          values={notificationDateValues}
          isBeginningOfYearGridComplete={sessionGridComplete.boy}
          isMiddleOfYearGridComplete={
            isMoyPanelEnabled && sessionGridComplete.moy
          }
          isEndOfYearGridComplete={isEoyPanelEnabled && sessionGridComplete.eoy}
          disabled={isSchoolInputReadOnly}
          onEditStateChange={handleNotificationEditStateChange}
        />
      </div>

      {showCancelModal && (
        <Modal
          type={DialogType.CONFIRM}
          open={showCancelModal}
          onClose={handleNoInCancel}
          title={'Are you sure?'}
        >
          <div className="confirm-container" data-testid="confirmation-modal">
            <p data-testid="cancel-message">
              Your changes have not been saved. Do you wish to continue?
            </p>
            <div className="button-row">
              <ActionButton
                onClick={handleYesInCancel}
                dataTestId="yes-button"
                tooltipText={'Navigate to the last page'}
              >
                <span>Yes</span>
              </ActionButton>
              <ActionButton
                classes="button--secondary"
                onClick={handleNoInCancel}
                dataTestId="no-button"
                tooltipText={'Stay on existing page'}
              >
                <span>No</span>
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </DistrictSchoolInfoPage>
  );
};

export default LiteracyScreeningSchoolInput;
